import { useEffect, useRef, useState } from "react";
import "./App.css";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { useMapEvents } from "react-leaflet";

function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng);
    },
  });
  return null;
}

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

function App() {
  const [city, setCity] = useState("");
  const [weather, setWeather] = useState(null);
  const [showList, setShowList] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const [accuracy, setAccuracy] = useState(null);
  const [coords, setCoords] = useState(null);
  const [myLocation, setMyLocation] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const itemRefs = useRef([]);
  const [isDark, setIsDark] = useState(false);

  const API_KEY = "19b23065ef748c602e824da08a043430"; // Replace with your OpenWeatherMap API key

  const districts = [
    "Sutturu",
    "kupparavalli",
    "Nagarle",
    "Hadinaru",
    "Bengaluru",
    "Mysore",
    "Mandya",
    "Ramanagara",
    "Chikkaballapur",
    "Kolar",
    "Tumakuru",
    "Chitradurga",
    "Davanagere",
    "Channarayapatna",
    "Arsikere",
    "Hassan",
    "Shravanabelagola",
    "Kanakapura",
    "Magadi",
    "Nelamangala",
    "Ramanagara",
    "Bidadi",
    "Anekal",
    "Kunigal",
    "Tumkur",
    "Hassan",
    "Chamarajanagar",
    "Gundlupet",
    "Nanjangud",
    "Hunsur",
    "Periyapatna",
    "Madikeri",
    "Virajpet",
    "Somwarpet",
    "Kodagu",
    "Puttur",
    "Sullia",
    "Karkala",
    "Uttara Kannada",
    "Sirsi",
    "Haliyal",
    "Dharwad",
    "Hubli-Dharwad",
    "Belagavi",
    "Gokak",
    "Athani",
    "Chikkodi",
    "Vijayapura",
    "Bagalkot",
    "Raichur",
    "Yadgir",
    "Gulbarga",
    "Bidar",
    "Kalaburagi",
    "Sedam",
    "Shorapur",
    "Dakshina Kannada",
    "Chennai",
    "Coimbatore",
    "Salem",
    "Erode",
    "Tiruppur",
    "Madurai",
    "Trichy",
    "Nagercoil",
    "Vellore",
    "Tirunelveli",
    "Kanchipuram",
    "Thanjavur",
    "Dindigul",
    "Karur",
    "Cuddalore",
    "Nagapattinam",
    "Ramanathapuram",
    "Virudhunagar",
    "Sivaganga",
    "Pudukkottai",
    "Ariyalur",
    "maddur",
    "malavalli",
    "kanakapura",
    "channapatna",
    "bangalore rural",
    "bangalore urban",  
    "vijayanagar",
    "rajajinagar",
    "jayanagar",
    "basavanagudi",
    "btm layout",
    "hsr layout",
    "whitefield",
    "electronic city",
    "hebbal",
    "yelahanka",
    "devanahalli",
    "sarjapur road",
    "marathahalli",
    "koramangala",
    "indiranagar",
    "bellandur",
    "kaggadasapura",
    "cv raman nagar",
    "mahadevapura",
    "kr puram",
    "rajiv gandhi nagar",
    "kanakpura road",
    "jigani",
    "attibele",
    "hosa road",
    "doddaballapur",
    "nelamangala",
    "magadi road",
    "tumkur road",
    "hosur road",
  ];

  const filteredList = districts.filter((d) =>
    d.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setShowList(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (activeIndex >= 0 && itemRefs.current[activeIndex]) {
      itemRefs.current[activeIndex].scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [activeIndex]);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "dark") {
      document.body.classList.add("dark");
    }
  }, []);

  const getWeatherByCity = async (name) => {
    if (!name) return;

    setLoading(true);
    setWeather(null);

    try {
      const res = await fetch(
        `https://api.openweathermap.org/data/2.5/weather?q=${name}&units=metric&appid=${API_KEY}`
      );
      const data = await res.json();

      setWeather(data);

      // ✅ SET COORDS FROM CITY SEARCH
      if (data.coord) {
        setCoords({
          latitude: data.coord.lat,
          longitude: data.coord.lon,
        });
      }

      setAccuracy(null); // city search = no GPS accuracy
    } catch {
      alert("Failed to fetch weather");
    }

    setLoading(false);
    setShowList(false);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation not supported");
      return;
    }

    setLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;

        // 👇 Store live GPS location
        setMyLocation({ latitude, longitude });
        setAccuracy(accuracy.toFixed(0));

        // Fetch weather
        const res = await fetch(
          `https://api.openweathermap.org/data/2.5/weather?lat=${latitude}&lon=${longitude}&units=metric&appid=${API_KEY}`
        );
        const data = await res.json();

        setWeather(data);
        setCity(data.name);
        setCoords({ latitude, longitude });
        setLoading(false);
      },
      () => {
        alert("Location permission denied");
        setLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const clearData = () => {
    setCity("");
    setWeather(null);
    setSearch("");
  };

  const getWeatherByCoords = async (lat, lon) => {
    setLoading(true);
    setWeather(null);

    try {
      const res = await fetch(
        `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&units=metric&appid=${API_KEY}`
      );
      const data = await res.json();

      setWeather(data);
      setCity(data.name || "Selected Location");
      setCoords({ latitude: lat, longitude: lon });
      setAccuracy(null); // map click ≠ GPS accuracy
    } catch {
      alert("Failed to fetch weather");
    }

    setLoading(false);
  };

  // Selected location pin
  const selectedIcon = new L.Icon({
    iconUrl: "https://cdn-icons-png.flaticon.com/512/684/684908.png",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
  });

  // Current location arrow
  const currentLocationIcon = new L.Icon({
    iconUrl: "https://cdn-icons-png.flaticon.com/512/149/149059.png",
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });

  return (
    <div className="app">
      <h1>🌤 Weather Forecast</h1>

      {/* INPUT */}
      <div className="input-box" ref={boxRef}>
        <input
          type="text"
          placeholder="Type or select city"
          value={city}
          onFocus={() => setShowList(true)}
          onChange={(e) => {
            setCity(e.target.value);
            setSearch(e.target.value);
            setShowList(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown" && showList && filteredList.length > 0) {
              e.preventDefault();
              setActiveIndex((prev) =>
                prev < filteredList.length - 1 ? prev + 1 : 0
              );
              return;
            }

            if (e.key === "ArrowUp" && showList && filteredList.length > 0) {
              e.preventDefault();
              setActiveIndex((prev) =>
                prev > 0 ? prev - 1 : filteredList.length - 1
              );
              return;
            }

            if (e.key === "Enter") {
              e.preventDefault();

              // ✅ Priority 1: dropdown selection
              if (showList && activeIndex >= 0 && filteredList[activeIndex]) {
                const selected = filteredList[activeIndex];
                setCity(selected);
                getWeatherByCity(selected);
              }
              // ✅ Priority 2: typed city
              else if (city.trim()) {
                getWeatherByCity(city.trim());
              }

              setShowList(false);
              setActiveIndex(-1);
            }
          }}
        />

        {showList && (
          <div className="dropdown">
            {filteredList.map((d, i) => (
              <div
                key={i}
                ref={(el) => (itemRefs.current[i] = el)}
                className={`dropdown-item ${i === activeIndex ? "active" : ""}`}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => {
                  setCity(d);
                  getWeatherByCity(d);
                  setShowList(false);
                  setActiveIndex(-1);
                }}
              >
                {d}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* BUTTONS */}
      <div className="btn-group">
        <div
          className="theme-toggle"
          onClick={() => {
            document.body.classList.toggle("dark");
            setIsDark((prev) => !prev);
          }}
        >
          <span className="icon"></span>
          <div className="toggle-track">
            <div className="toggle-thumb"></div>
          </div>
          <span className="icon"></span>
        </div>

        <button
          disabled={!city || loading}
          onClick={() => getWeatherByCity(city)}
        >
          Get Weather
        </button>
        <button className="gps" onClick={useMyLocation} disabled={loading}>
          Location
        </button>

        <button className="clear" onClick={clearData}>
          Clear
        </button>
      </div>

      {/* LOADER */}
      {loading && <div className="loader"></div>}

      {/* WEATHER + MAP (SIDE BY SIDE) */}
      {weather && weather.main && !loading && (
        <div className="content-row">
          {/* WEATHER CARD */}
          <div className="weather-section">
            <div className="weather-card">
              <h2>{weather.name}</h2>
              <p>🌡 {weather.main.temp}°C</p>
              <p>☁ {weather.weather[0].main}</p>
              <p>💧 Humidity: {weather.main.humidity}%</p>
            </div>

            {accuracy && (
              <p className="accuracy">📍 GPS Accuracy: ±{accuracy} meters</p>
            )}
          </div>

          {/* MAP */}
          {coords && (
            <div className="map-container">
              <MapContainer
                key={`${coords.latitude}-${coords.longitude}-${
                  isDark ? "dark" : "light"
                }`}
                center={[coords.latitude, coords.longitude]}
                zoom={13}
                zoomControl
                scrollWheelZoom
                dragging
                className="leaflet-map"
              >
                <TileLayer
                  url={
                    document.body.classList.contains("dark")
                      ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                      : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  }
                  attribution={
                    document.body.classList.contains("dark")
                      ? "© OpenStreetMap © CartoDB"
                      : "© OpenStreetMap"
                  }
                />

                {/* Click to select new location */}
                <MapClickHandler
                  onMapClick={(latlng) => {
                    getWeatherByCoords(latlng.lat, latlng.lng);
                  }}
                />

                {/* Selected location pin */}
                <Marker
                  position={[coords.latitude, coords.longitude]}
                  icon={selectedIcon}
                >
                  <Popup>Selected Location</Popup>
                </Marker>

                {/* My current location arrow */}
                {myLocation && (
                  <Marker
                    position={[myLocation.latitude, myLocation.longitude]}
                    icon={currentLocationIcon}
                  >
                    <Popup>My Current Location</Popup>
                  </Marker>
                )}
              </MapContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default App;
